import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  fetchProceduresList,
  addProcedureFaq,
  listProcedureFaq,
  editProcedureFaq,
  updateProcedureFaq,
  trashProcedureFaq,
} from "../../Adapters/ApiAdapter";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProcedureFaqList = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editFaq, setEditFaq] = useState(null);
  const [deleteFaqId, setDeleteFaqId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [procedures, setProcedures] = useState([]);
  const [selectedProcedureId, setSelectedProcedureId] = useState("");
  const [newFaqs, setNewFaqs] = useState([{ question: "", answer: "" }]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [faqRes, procRes] = await Promise.all([
          listProcedureFaq(),
          fetchProceduresList()
        ]);
        setFaqs(faqRes?.data || []);
        setProcedures(procRes?.procedures || []);
      } catch (err) {
        setError("Failed to load FAQ data.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editProcedureFaq(id);
      setEditFaq(response?.data);
    } catch (err) {
      toast.error("Failed to load FAQ details.");
    }
  };

  const handleAddFaq = async () => {
    if (!selectedProcedureId) {
      toast.error("Please select a Procedure.");
      return;
    }

    setActionLoading(true);
    try {
      const addedFaqs = [];
      for (const faq of newFaqs) {
        if (!faq.question.trim() || !faq.answer.trim()) continue;

        const formData = new FormData();
        formData.append("procedure_id", selectedProcedureId);
        formData.append("question", faq.question);
        formData.append("answer", faq.answer);

        const response = await addProcedureFaq(formData);
        if (response?.data) {
          addedFaqs.push(response.data);
        }
      }

      if (addedFaqs.length > 0) {
        toast.success("FAQs added successfully!");
        // Reload list to get names correctly
        const reload = await listProcedureFaq();
        setFaqs(reload?.data || []);
      }
      setNewFaqs([{ question: "", answer: "" }]);
      setSelectedProcedureId("");
      setAddDialogOpen(false);
    } catch (err) {
      toast.error("Failed to add FAQs.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("question", editFaq.question);
    formData.append("answer", editFaq.answer);
    formData.append("procedure_id", editFaq.procedure_id);

    setActionLoading(true);
    try {
      await updateProcedureFaq(editFaq.id, formData);
      toast.success("FAQ updated successfully!");
      const reload = await listProcedureFaq();
      setFaqs(reload?.data || []);
      setEditFaq(null);
    } catch (err) {
      toast.error("Failed to update FAQ.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setActionLoading(true);
    try {
      await trashProcedureFaq(id);
      toast.success("FAQ deleted successfully!");
      setFaqs((prev) => prev.filter((faq) => faq.id !== id));
      setDeleteFaqId(null);
    } catch (err) {
      toast.error("Failed to delete FAQ.");
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "question", headerName: "Question", flex: 1 },
    { field: "answer", headerName: "Answer", flex: 2 },
    { field: "procedure_name", headerName: "Procedure", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            style={{ marginLeft: 8 }}
            onClick={() => setDeleteFaqId(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m={3}>
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Procedure FAQs
      </Typography>

      <Box mb={2}>
        <Button
          variant="contained"
          color="success"
          onClick={() => setAddDialogOpen(true)}
          style={{ background: '#0ca84f' }}
        >
          Add New FAQ
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid 
          rows={faqs} 
          columns={columns} 
          autoHeight 
          getRowId={(row) => row.id}
        />
      )}

      {/* Edit Dialog */}
      {editFaq && (
        <Dialog open onClose={() => setEditFaq(null)} fullWidth maxWidth="sm">
          <DialogTitle>Edit FAQ</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Question"
              value={editFaq.question}
              onChange={(e) => setEditFaq({ ...editFaq, question: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Answer"
              value={editFaq.answer}
              multiline
              rows={4}
              onChange={(e) => setEditFaq({ ...editFaq, answer: e.target.value })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Procedure</InputLabel>
              <Select
                value={editFaq.procedure_id}
                onChange={(e) => setEditFaq({ ...editFaq, procedure_id: e.target.value })}
                label="Procedure"
              >
                {procedures.map((proc) => (
                  <MenuItem key={proc.id} value={proc.id}>
                    {proc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditFaq(null)} variant="outlined">Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Add FAQ Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add New Procedure FAQ</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Procedure</InputLabel>
            <Select
              value={selectedProcedureId}
              onChange={(e) => setSelectedProcedureId(e.target.value)}
              label="Procedure"
              required
            >
              {procedures.map((proc) => (
                <MenuItem key={proc.id} value={proc.id}>
                  {proc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {newFaqs.map((faq, index) => (
            <Box key={index} mt={2} p={2} border="1px solid #ddd" borderRadius="4px">
              <TextField
                fullWidth
                label={`Question ${index + 1}`}
                value={faq.question}
                onChange={(e) => {
                  const updatedFaqs = [...newFaqs];
                  updatedFaqs[index].question = e.target.value;
                  setNewFaqs(updatedFaqs);
                }}
                margin="dense"
              />
              <TextField
                fullWidth
                label={`Answer ${index + 1}`}
                value={faq.answer}
                multiline
                rows={3}
                onChange={(e) => {
                  const updatedFaqs = [...newFaqs];
                  updatedFaqs[index].answer = e.target.value;
                  setNewFaqs(updatedFaqs);
                }}
                margin="dense"
              />
            </Box>
          ))}

          <Button
            variant="outlined"
            onClick={() => setNewFaqs([...newFaqs, { question: "", answer: "" }])}
            style={{ marginTop: "10px" }}
          >
            + Add Another FAQ
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button variant="contained" onClick={handleAddFaq} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      {deleteFaqId && (
        <Dialog open onClose={() => setDeleteFaqId(null)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>Are you sure you want to delete this FAQ?</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteFaqId(null)} variant="outlined">Cancel</Button>
            <Button variant="contained" color="error" onClick={() => handleDelete(deleteFaqId)} disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ProcedureFaqList;
